import { useEffect, useRef, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';

export type UserInsightsRange = 'day' | 'week' | 'month';

export interface UserInsightsMetrics {
  total: number | null;
  active: number | null;
  newToday: number | null;
  newThisWeek: number | null;
  newThisMonth: number | null;
  growthWoW: number | null;
  growthMoM: number | null;
  byAuthProvider: Record<string, number> | null;
}

export interface UserInsightsSeriesPoint {
  ts: number;
  count: number;
}

export interface UserInsightsResponse {
  asOf: number;
  range: UserInsightsRange;
  metrics: UserInsightsMetrics;
  cumulative: UserInsightsSeriesPoint[];
  newSignups: UserInsightsSeriesPoint[];
}

const POLL_INTERVAL_MS = 300_000; // 5 minutes

export const useUserInsights = (
  range: UserInsightsRange,
): { data: UserInsightsResponse | null; loading: boolean; error: string | null } => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [data, setData] = useState<UserInsightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchInsights = async (): Promise<void> => {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        if (isMounted) {
          setError('REACT_APP_API_URL environment variable is not set');
          setLoading(false);
        }
        return;
      }

      try {
        let token: string | null = null;
        const currentAccounts = accountsRef.current;
        if (currentAccounts.length > 0) {
          token = await acquireApiToken(instance, currentAccounts[0]);
        }

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(
          `${apiUrl}/admin/users/insights?range=${encodeURIComponent(range)}`,
          { method: 'GET', headers },
        );
        const body = await response.json();

        if (!response.ok) {
          throw new Error(body.error || `HTTP ${response.status}`);
        }

        if (isMounted) {
          setData(body as UserInsightsResponse);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? 'Unknown error');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const tick = async (): Promise<void> => {
      await fetchInsights();
      if (isMounted) {
        timer = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    tick();

    return () => {
      isMounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [isAuthenticated, instance, range]);

  return { data, loading, error };
};
